import { createContext, useContext, useEffect, useState } from "react";
import { useAuth as useClerkAuth, useUser as useClerkUser, useOrganization } from "@clerk/clerk-react";
import { logger } from "@/lib/logger";
import { setCurrentOrganizationId } from "@/utils/getCurrentOrganizationId";
import { monitoring } from "@/lib/monitoring";

type AuthContextType = {
  user: any | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const clerkAuth = useClerkAuth();
  const clerkUser = useClerkUser();
  const { organization } = useOrganization();

  // Handle Clerk auth state changes
  useEffect(() => {
    logger.log("Using Clerk authentication");
    
    if (clerkAuth?.isLoaded) {
      if (clerkAuth.isSignedIn && clerkUser?.user) {
        // Set the Clerk user directly
        const userData = {
          id: clerkUser.user.id,
          email: clerkUser.user.primaryEmailAddress?.emailAddress || '',
          fullName: clerkUser.user.fullName,
          imageUrl: clerkUser.user.imageUrl,
          createdAt: clerkUser.user.createdAt?.toISOString() || '',
          updatedAt: clerkUser.user.updatedAt?.toISOString() || '',
        };
        setUser(userData);
        
        // Track user in monitoring system
        monitoring.setUser({
          id: userData.id,
          email: userData.email,
          username: userData.fullName || undefined,
        });
        monitoring.trackUserActivity(userData.id);
        logger.log("User tracked in monitoring:", userData.id);
      } else {
        setUser(null);
        // Clear user from monitoring
        monitoring.clearUser();
      }
      setIsLoading(false);
    }
  }, [clerkAuth?.isLoaded, clerkAuth?.isSignedIn, clerkUser?.user]);

  // Initialize organization ID when organization changes
  useEffect(() => {
    if (organization?.id) {
      logger.log("Setting organization ID:", organization.id);
      setCurrentOrganizationId(organization.id);
    }
  }, [organization?.id]);

  const signOut = async () => {
    try {
      logger.log("Executing sign out");
      setIsLoading(true);
      
      // Clear the state first for immediate UI update
      setUser(null);
      
      // Clear user from monitoring
      monitoring.clearUser();
      
      // Sign out from Clerk
      await clerkAuth?.signOut();
      
      logger.log("Sign out successful");
    } catch (error) {
      logger.error("Sign out error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
};